import { fromAPI } from "@cur8/rich-entity";
import { skipToken, useQuery } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { fetchPages } from "lib/fetchPages";
import { useAPIClient } from "render/context/APIContext";
import { ExtractBody, ExtractKey, QueryOptions } from "typings/query";

// GET /api/v1/measurement/{patientId} orders results newest-first (backend
// queries Cosmos with OrderByDescending(TimestampStart) before filtering by
// metric), so the first item in the first page that isn't from the current
// visit is already the latest. We only page further in the edge case where
// every item on a page belongs to the excluded visit.
export function usePreviousMeasurementQuery<T = Body>({
  patientId,
  metricName,
  excludeVisitId,
  options = {},
}: {
  patientId: string | undefined;
  metricName: string | undefined;
  excludeVisitId: string | undefined;
  options?: QueryOptions<Body, Key, T>;
}) {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: queryKey(patientId, metricName, excludeVisitId),
    queryFn:
      patientId && metricName
        ? queryFn(apiClient, patientId, metricName, excludeVisitId)
        : skipToken,
    staleTime: Infinity,
    ...options,
  });
}

export const queryKey = (
  patientId: string | undefined,
  metricName: string | undefined,
  excludeVisitId: string | undefined
) => ["previous-measurement", patientId, metricName, excludeVisitId];

const queryFn =
  (
    apiClient: APIClient,
    patientId: string,
    metricName: string,
    excludeVisitId: string | undefined
  ) =>
  async () => {
    const pages = fetchPages(
      (continuationToken) =>
        apiClient.measurement.queryMeasurements({
          patientId,
          metricName,
          pageSize: 100,
          continuationToken,
        }).result
    );

    for await (const items of pages) {
      for (const dto of items) {
        const m = fromAPI.toMeasurement(dto);
        if (m.visitId !== excludeVisitId) {
          return m;
        }
      }
    }

    // null, not undefined — React Query treats an undefined result as an error.
    return null;
  };

type Body = ExtractBody<typeof queryFn>;
type Key = ExtractKey<typeof queryKey>;
